import React, { useState, useEffect, useRef } from 'react'
import { Dropdown, message } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import { useClickOutside, useDebounce, useKeyPress } from 'Hooks'
import '../index.scss'
import {
  SearchDropdownProps,
  ViewModeState,
  ImportState,
  ProductInfo
} from './types'
import {
  hasCompleteProductInfo,
  findMatchingOption,
  injectStyles,
  isProductSegmentType
} from './utils'
import {
  LOADING_SPINNER_STYLES,
  PERFORMANCE_THRESHOLD,
  DEBOUNCE_DELAY
} from './constants'
import { useProductInfo } from './hooks/useProductInfo'
import { useProductSearch } from './hooks/useProductSearch'
import ProductImportModal from './components/ProductImportModal'
import MenuDropdown from './components/MenuDropdown'
import ProductListDropdown from './components/ProductListDropdown'
import productImagePlaceholder from 'Assets/images/product_img_placeholder.png'

// Inject loading spinner styles
injectStyles('search-dropdown-styles', LOADING_SPINNER_STYLES)

const SearchDropdown: React.FC<SearchDropdownProps> = ({
  values,
  options,
  placeholder,
  isMulti,
  customDropdown,
  onChange,
  handleSearch,
  maxWidth,
  segmentType,
  hasMore,
  scrollRef,
  sentryRef,
  loading,
  onLoadMore,
  selectedProductsInfo,
  onSelectedProductsInfoChange,
}) => {
  // UI State
  const [isVisible, setIsVisible] = useState(false)
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined)
  
  // View Mode State
  const [viewMode, setViewMode] = useState<ViewModeState>({
    isViewMode: false,
    selectedTempValues: [],
    tempValues: []
  })
  
  // Import State
  const [importState, setImportState] = useState<ImportState>({
    isImportModalVisible: false,
    importLoading: false
  })
  
  // Performance state
  const [skipSearchForPerformance, setSkipSearchForPerformance] = useState(false)
  
  // Separate state for view mode search results
  const [viewModeSearchResults, setViewModeSearchResults] = useState<any[]>([])
  
  // Custom hooks
  const { 
    localProductsInfo, 
    updateProductInfo, 
    removeProductInfo 
  } = useProductInfo({ 
    selectedProductsInfo, 
    onSelectedProductsInfoChange 
  })
  
  const { 
    searchingMissingProducts, 
    searchProgress, 
    searchedProductIds,
    searchMissingProductsForViewMode 
  } = useProductSearch({ 
    segmentType, 
    updateProductInfo 
  })
  
  // Refs and outside click handling
  const dropdownRef = useRef(null)
  const ref = useRef(null)
  
  useClickOutside(ref, () => {
    setIsVisible(false)
    onCloseDropdown()
  }, dropdownRef)
  
  useClickOutside(ref, () => {
    setIsVisible(false)
    onCloseDropdown()
  }, scrollRef)
  
  useKeyPress('Enter', (isKeyPressed: boolean) => {
    setIsVisible(!isKeyPressed)
    onCloseDropdown()
  })
  
  useKeyPress('Escape', (isKeyPressed: boolean) => {
    setIsVisible(!isKeyPressed)
    onCloseDropdown()
  })
  
  const debounceSearchQuery = useDebounce(searchQuery, DEBOUNCE_DELAY)
  
  // Check if this is a product-related segment type
  const isRenderProductList = isProductSegmentType(segmentType)
  
  const onCloseDropdown = () => {
    setViewMode({
      isViewMode: false,
      selectedTempValues: [],
      tempValues: []
    })
  }
  
  // Debounced search effect
  useEffect(() => {
    if (debounceSearchQuery !== undefined && handleSearch) {
      handleSearch(debounceSearchQuery)
    }
  }, [debounceSearchQuery, handleSearch])
  
  // Intersection Observer for load more functionality
  useEffect(() => {
    if (!sentryRef?.current || !onLoadMore || viewMode.isViewMode) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && hasMore && !loading) {
          onLoadMore()
        }
      },
      {
        threshold: 0.1,
        rootMargin: '20px',
      }
    )

    observer.observe(sentryRef.current)

    return () => {
      if (sentryRef?.current) {
        observer.unobserve(sentryRef.current)
      }
    }
  }, [sentryRef, onLoadMore, hasMore, loading, viewMode.isViewMode])
  
  // Effect to search for missing products when entering view mode
  useEffect(() => {
    if (viewMode.isViewMode && values && values.length > 0) {
      const missingProductIds = values.filter(value => 
        !hasCompleteProductInfo(value, localProductsInfo)
      )
      
      if (missingProductIds.length > PERFORMANCE_THRESHOLD && !skipSearchForPerformance) {
        setSkipSearchForPerformance(true)
        missingProductIds.forEach(id => {
          updateProductInfo(id, { label: id, value: id, image: productImagePlaceholder })
        })
      } else if (missingProductIds.length > 0 && !skipSearchForPerformance) {
        searchMissingProductsForViewMode(missingProductIds)
      }
    }
  }, [viewMode.isViewMode, values, skipSearchForPerformance, localProductsInfo, searchMissingProductsForViewMode, updateProductInfo])
  
  // Effect to update local product info when new options arrive from search
  useEffect(() => {
    if (options && !viewMode.isViewMode && values) {
      let updatedCount = 0
      
      // Update local product info with newly found products
      options.forEach(option => {
        const isValueMatch = values.includes(option.value)
        const hasExistingInfo = localProductsInfo[option.value]
        
        if (isValueMatch && !hasExistingInfo) {
          updateProductInfo(option.value, option)
          updatedCount++
        }
      })
      
      // Also check if we need to match by ID
      if (updatedCount === 0) {
        values.forEach(value => {
          const matchingOption = findMatchingOption(value, options)
          
          if (matchingOption && !localProductsInfo[value]) {
            const properProductInfo = {
              ...matchingOption,
              label: matchingOption.label || (matchingOption as any).name || value,
              value: value,
              image: matchingOption.image || (matchingOption as any).img || productImagePlaceholder
            }
            
            updateProductInfo(value, properProductInfo)
            updatedCount++
          }
        })
      }
    }
  }, [options, viewMode.isViewMode, values, localProductsInfo, updateProductInfo])
  
  // Auto-populate product info from existing options
  useEffect(() => {
    if (values && values.length > 0 && options && options.length > 0) {
      values.forEach(value => {
        const existingOption = options.find(opt => opt.value === value)
        if (existingOption && !localProductsInfo[value]) {
          updateProductInfo(value, existingOption)
        }
      })
    }
  }, [values, options, localProductsInfo, updateProductInfo])
  
  const computeLabel = () => {
    const chosenLabels = (Array?.isArray(values) ? values : [values])?.map(
      (item) => options?.find((op) => op.value === item)?.label || item
    )
    return (
      <div className="truncate w-full">
        {chosenLabels?.length ? (
          isRenderProductList ? (
            `Product (${chosenLabels?.length})`
          ) : (
            chosenLabels?.map((item, index) => {
              if (index > 0) {
                item = ', ' + item
              }
              return item
            })
          )
        ) : (
          <span className="text-disabled_text">{placeholder}</span>
        )}
      </div>
    )
  }
  
  // Handle product import
  const handleProductImport = async (productIds: string[]) => {
    try {
      setImportState(prev => ({ ...prev, importLoading: true }))
      
      // Check if products exist in options
      const existingProductIds = options?.map(option => option.value) || []
      const newProductIds = productIds.filter(id => !existingProductIds.includes(id))
      
      if (newProductIds.length === 0) {
        const newValues = [...new Set([...(values || []), ...productIds])]
        onChange(newValues)
        
        productIds.forEach(id => {
          const existingOption = options?.find(option => option.value === id)
          if (existingOption) {
            updateProductInfo(id, existingOption)
          }
        })
        
        message.success(`${productIds.length} products added to selection`)
        setImportState(prev => ({ ...prev, isImportModalVisible: false }))
        return
      }
      
      // Create placeholder products for new IDs
      const fetchedProducts = newProductIds.map(id => ({
        label: id,
        value: id,
        image: productImagePlaceholder
      }))
      
      // Store product info
      fetchedProducts.forEach(product => {
        updateProductInfo(product.value, product)
      })
      
      // Update selected values
      const newValues = [...new Set([...(values || []), ...productIds])]
      onChange(newValues)
      
      message.success(`${productIds.length} products added to selection`)
      setImportState(prev => ({ ...prev, isImportModalVisible: false }))
      
    } catch (error) {
      console.error('Import error:', error)
      message.error('Error importing products')
    } finally {
      setImportState(prev => ({ ...prev, importLoading: false }))
    }
  }
  
  // Helper function to get merged options for view mode
  const getMergedOptionsForViewMode = (): ProductInfo[] => {
    if (!viewMode.isViewMode) return options || []
    
    const selectedValues = values || []
    const existingOptions = options || []
    const existingOptionValues = new Set(existingOptions.map(opt => opt.value))
    
    // Find missing selected items
    const missingSelectedItems = selectedValues
      .filter(value => !existingOptionValues.has(value))
      .map(value => {
        const storedInfo = localProductsInfo[value]
        return storedInfo || {
          label: value, 
          value: value,
          image: productImagePlaceholder
        }
      })
    
    // Combine with existing selected options
    const selectedExistingOptions = existingOptions.filter(option => 
      selectedValues.includes(option.value)
    )
    
    return [...missingSelectedItems, ...selectedExistingOptions]
  }
  
  // Render dropdown content
  const renderDropdownContent = () => {
    if (customDropdown) {
      return (
        <div
          ref={dropdownRef}
          style={{
            maxHeight: 400,
            overflow: 'scroll',
            padding: 8,
            boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.12)',
            backgroundColor: 'white',
          }}
        >
          {customDropdown()}
        </div>
      )
    }
    
    if (isRenderProductList) {
      return (
        <div ref={dropdownRef}>
          <ProductListDropdown
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            values={values}
            options={options}
            isMulti={isMulti}
            onChange={onChange}
            onImportClick={() => setImportState(prev => ({ ...prev, isImportModalVisible: true }))}
            maxWidth={maxWidth}
            isViewMode={viewMode.isViewMode}
            searchingMissingProducts={searchingMissingProducts}
            searchProgress={searchProgress}
            localProductsInfo={localProductsInfo}
            hasMore={hasMore}
            loading={loading}
            onLoadMore={onLoadMore}
            scrollRef={scrollRef}
            sentryRef={sentryRef}
            selectedTempValues={viewMode.selectedTempValues}
            onSelectedTempValuesChange={(values) => setViewMode(prev => ({ ...prev, selectedTempValues: values }))}
            onEnterViewMode={() => {
              setViewMode({
                isViewMode: true,
                selectedTempValues: values || [],
                tempValues: values || []
              })
            }}
            onExitViewMode={() => {
              onCloseDropdown()
            }}
            onSaveViewMode={(selectedValues) => {
              onChange(selectedValues)
              onCloseDropdown()
            }}
            skipSearchForPerformance={skipSearchForPerformance}
            onLoadProductDetails={() => {
              setSkipSearchForPerformance(false)
              // Trigger search for missing products
              const missingProductIds = (values || []).filter(value => {
                return !hasCompleteProductInfo(value, localProductsInfo)
              })
              if (missingProductIds.length > 0) {
                searchMissingProductsForViewMode(missingProductIds)
              }
            }}
            updateProductInfo={updateProductInfo}
            removeProductInfo={removeProductInfo}
          />
        </div>
      )
    }
    
    return (
      <div ref={dropdownRef}>
        <MenuDropdown
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          values={values}
          options={options}
          isMulti={isMulti}
          onChange={onChange}
          maxWidth={maxWidth}
        />
      </div>
    )
  }
  
  return (
    <>
      <Dropdown
        getPopupContainer={(trigger: any) => trigger.parentNode}
        visible={isVisible}
        trigger={['click']}
        overlay={renderDropdownContent()}
        className="w-full"
      >
        <div
          ref={ref}
          className="py-2 px-3 rounded cursor-pointer flex justify-between items-center border border-gray_5"
          style={{ height: 40, maxWidth: maxWidth || 210 }}
          onClick={() => setIsVisible(true)}
        >
          <span className="flex w-4/5" style={{ minWidth: '100px' }}>
            <span className="w-full">{computeLabel()}</span>
          </span>
          <DownOutlined className="ml-2 text-xs" />
        </div>
      </Dropdown>
      
      <ProductImportModal 
        visible={importState.isImportModalVisible}
        onCancel={() => setImportState(prev => ({ ...prev, isImportModalVisible: false }))}
        onImport={handleProductImport}
        loading={importState.importLoading}
      />
    </>
  )
}

export default SearchDropdown 